// Extract RedGifs video ID from various URL formats
export const getRedGifsId = (url: string | null | undefined): string | null => {
  if (!url) return null;

  // Match watch URL: https://redgifs.com/watch/VideoId
  const watchMatch = url.match(/redgifs\.com\/watch\/([^/?]+)/i);
  if (watchMatch) return watchMatch[1]?.toLowerCase() || null;

  // Match media URL: https://media.redgifs.com/VideoId.mp4 or VideoId-mobile.mp4
  const mediaMatch = url.match(/media\.redgifs\.com\/([^.-]+)/i);
  if (mediaMatch) return mediaMatch[1]?.toLowerCase() || null;

  // Match ifr URL: https://redgifs.com/ifr/VideoId
  const ifrMatch = url.match(/redgifs\.com\/ifr\/([^/?]+)/i);
  if (ifrMatch) return ifrMatch[1]?.toLowerCase() || null;

  return null;
};

// Get iframe embed URL for various video sources (RedGifs, Google Drive, etc.)
export const getRedGifsEmbedUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;

  // Google Drive: convert /view to /preview for embedding
  if (url.includes('drive.google.com')) {
    const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
    if (match) {
      return `https://drive.google.com/file/d/${match[1]}/preview`;
    }
  }

  // RedGifs
  const id = getRedGifsId(url);
  if (id) {
    return `https://www.redgifs.com/ifr/${id}`;
  }

  return null;
};

// Get RedGifs thumbnail URL via our proxy (avoids CORS/DNS issues)
export const getRedGifsThumbnail = (url: string | null | undefined): string | null => {
  const id = getRedGifsId(url);
  if (!id) return null;
  return `/api/thumbnail/${id}`;
};

// Get native video URL via our proxy (avoids CORS/DNS issues, supports seeking)
export const getRedGifsVideoUrl = (url: string | null | undefined, quality: 'hd' | 'sd' | 'silent' = 'hd'): string | null => {
  const id = getRedGifsId(url);
  if (!id) return null;
  return `/api/video/${id}?quality=${quality}`;
};

// Helper to get RedGifs watch URL
export const getRedGifsWatchUrl = (url: string | null | undefined): string => {
  const id = getRedGifsId(url);
  if (id) return `https://redgifs.com/watch/${id}`;
  return url || '';
};

// Check if URL is a RedGifs URL
export const isRedGifsUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  return url.includes('redgifs.com') || url.includes('redgifs');
};

// Validate a clip URL - returns error message or null if valid
export const validateClipUrl = (url: string | null | undefined): string | null => {
  if (!url || !url.trim()) {
    return 'URL is required';
  }

  const trimmedUrl = url.trim();

  // Check if it's a valid URL format
  try {
    new URL(trimmedUrl);
  } catch {
    return 'Please enter a valid URL';
  }

  // Check if it's a RedGifs URL
  if (!isRedGifsUrl(trimmedUrl)) {
    return 'Please enter a RedGifs URL (redgifs.com)';
  }

  // Check if we can extract a video ID
  const videoId = getRedGifsId(trimmedUrl);
  if (!videoId) {
    return 'Could not find video ID in URL. Use format: redgifs.com/watch/videoname';
  }

  return null; // Valid!
};

// Normalize a RedGifs URL to standard watch format
export const normalizeRedGifsUrl = (url: string): string => {
  const id = getRedGifsId(url);
  if (id) {
    return `https://redgifs.com/watch/${id}`;
  }
  return url;
};

// Generate a random code for reviewers
export const generateReviewerCode = (): string => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = 'VBR-';
  for (let i = 0; i < 6; i++) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
};

// Generate a random code for couples (shareable - for friends to recommend)
export const generateCoupleCode = (): string => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = 'VBC-';
  for (let i = 0; i < 6; i++) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
};

// Generate a private access code for couples (login only)
export const generateAccessCode = (): string => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = 'VBA-';
  for (let i = 0; i < 6; i++) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
};

// Generate a unique ID
export const generateId = (prefix: string): string => {
  return `${prefix}-${Math.floor(Math.random() * 9000) + 1000}`;
};

// Get or create a user ID for private storage
export const getUserId = (): string => {
  const KEY = 'vb-user-id';
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(KEY, id);
  }
  return id;
};

// Format date for display
export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString();
};

// Get current ISO date string
export const now = (): string => new Date().toISOString();

// Get today's date in YYYY-MM-DD format
export const today = (): string => new Date().toISOString().split('T')[0] ?? '';
